import { useCallback, useEffect, useMemo, useState } from 'react'

import { getLaborTableMetrics } from '@/api/clarity'
import { ScalrTableColumn } from '@/components/customComponents'
import { formatMetric } from '@/hooks/clarity/util'
import { LaborTableMetricsModel } from '@/types/clarity'

const COMPANY_SYSTEM = 'System Total'

const laborTableColumns: ScalrTableColumn[] = [
  { header: 'Employee', accessorKey: 'employeeName', type: 'text' },
  {
    header: 'Total Hours',
    accessorKey: 'totalHours',
    type: 'hour',
  },
  {
    header: 'Total Cost',
    accessorKey: 'totalCost',
    type: 'money',
  },
  {
    header: 'Total Cost Percentage',
    accessorKey: 'totalCostPercentage',
    type: 'percentage',
  },
  {
    header: 'AOV by Employee',
    accessorKey: 'aovByEmployee',
    type: 'money',
  },
  {
    header: 'Tip Collected',
    accessorKey: 'tipCollected',
    type: 'money',
  },
  {
    header: 'Tip/Net Sales',
    accessorKey: 'tipPercentage',
    type: 'percentage',
  },
  {
    header: 'Total Overtime',
    accessorKey: 'totalOvertime',
    type: 'hour',
  },
]

export default function useLaborTableMetrics(
  startDate: string,
  endDate: string,
  branches: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [laborTableData, setLaborTableData] = useState<
    LaborTableMetricsModel[]
  >([])

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { laborTableMetrics } = await fetchLaborTableMetrics(
          branches,
          startDate,
          endDate
        )

        setLaborTableData(laborTableMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData])

  // Define refetch function
  const refetch = useCallback(() => {
    console.log('Refetching labor table metrics data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    laborTableData,
    laborTableColumns,
    loading,
    error,
    refetch,
  }
}

async function fetchLaborTableMetrics(
  branches: string,
  startDate: string,
  endDate: string
): Promise<{
  laborTableMetrics: LaborTableMetricsModel[]
}> {
  try {
    const response = await getLaborTableMetrics(branches, startDate, endDate)

    const metrics: LaborTableMetricsModel[] = response.map(
      (responseMetrics: any) => {
        return setMetric(
          // responseMetrics.employee_id,
          responseMetrics.employee_name,
          responseMetrics.branch_name,
          responseMetrics.total_hours,
          responseMetrics.total_cost,
          responseMetrics.total_cost_percentage,
          responseMetrics.aov_by_employee,
          responseMetrics.tip_collected,
          responseMetrics.tip_percentage,
          responseMetrics.total_overtime,
          responseMetrics.overtime_percentage
        )
      }
    )

    const filteredMetrics = metrics.filter(
      (metric) => metric.employeeName !== COMPANY_SYSTEM
    )

    const laborTableMetrics = filteredMetrics.slice(0, 5)

    return {
      laborTableMetrics,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function setMetric(
  // id: string,
  employeeName: string,
  branchName: string,
  totalHours: number,
  totalCost: number,
  totalCostPercentage: number,
  aovByEmployee: number,
  tipCollected: number,
  tipPercentage: number,
  totalOvertime: number,
  overtimePercentage: number
): LaborTableMetricsModel {
  return {
    // id,
    employeeName: employeeName,
    branchName: branchName,
    totalHours: formatMetric(totalHours, 0),
    totalCost: formatMetric(totalCost, 0),
    totalCostPercentage: formatMetric(totalCostPercentage, 1),
    aovByEmployee: formatMetric(aovByEmployee, 2),
    tipCollected: formatMetric(tipCollected, 0),
    tipPercentage: formatMetric(tipPercentage, 1),
    totalOvertime: formatMetric(totalOvertime, 2),
    overtimePercentage: formatMetric(overtimePercentage, 2),
  }
}
